import React from 'react';
import {EmptyState} from './EmptyState';
import {FileCard} from './FileCard';
import {Scrollbar} from '@priority-software/priority-style-react';
import {SearchBlock} from '../../Common/components/SearchBlock';
import {fetchMore} from '../../Common/actions';
import {Record} from '../../../../../../stores/Form/Record';
import {FileUploader} from './FileUploader';
import {priority, common} from '@priority-software/priority-proto-api/lib';
import styles from '../styles/content.module.scss';
import {useCallback, useEffect, useRef, useState} from 'react';
import {FileFields, MailboxSendFields} from '../consts';
import {APIActionStatus, Column, Field, formsStore} from '../../../../../../stores/Form';
import {saveRow} from '../../Common/actions';
import {mapValueByColumnType} from '../../../../../../stores/Form/utils';
import texts from '../../texts';
import {undoAll} from '../../../../actions/undo';
import {AttachmentType, FileUploaderController} from '../../../../../../components/FileUploader';
import {createPriorityFileObj} from '../utils';
import {SettingsPanelCol} from '../../Common/consts';
import {ProgressBar} from './ProgressBar';
import {fieldUpdate} from '../../../../actions/standAloneForm';

type ContentProps = {
    files: Record[];
    shouldFetchMore: boolean;
    fastSearchQuery: string;
    enabledCols: string[];
    applySettings: (cols: SettingsPanelCol[]) => Promise<void>;
    subformType: priority.netitems.form.SubformType;
    setParentNeedsRefresh: (value: boolean) => void;
};

export const Content = ({
    files,
    shouldFetchMore,
    fastSearchQuery,
    enabledCols,
    applySettings,
    subformType,
    setParentNeedsRefresh
}: ContentProps) => {
    const contentWrapperRef = useRef(null);
    const [searchQuery, setSearchQuery] = useState('');
    const handleScrollEnd = () => {
        if (shouldFetchMore) {
            fetchMore(files[0].parentForm, files);
        }
    };
    const filesCount = useRef(0);
    const successFilesCount = useRef(0);
    const [isUploading, setIsUploading] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const timer = useRef(null);

    const uploadFiles = useCallback(async (files: File[]) => {
        const uploadFile = async (file: File, index: number) => {
            setIsUploading(true);
            setIsSuccess(false);
            const attchmentObj = await createPriorityFileObj(file);
            // update name
            let trimmedName;
            const curForm = formsStore.getActiveForm();
            if (curForm?.parentForm?.ename !== 'MAILBOXSEND') {
                const nameColumn: Column = curForm.getColumnByName(FileFields.fileName);
                const maxLength = nameColumn.maxLength;
                const fileNameArr = file.name.split('.');
                fileNameArr.pop();
                const fileName = fileNameArr.join('.');
                trimmedName = fileName.substring(0, maxLength);
            }
            await onFileChange(attchmentObj, trimmedName, index);
        };

        filesCount.current = files.length;
        successFilesCount.current = 0;
        for (let i = 0; i < files.length; i++) {
            await uploadFile(files[i], i);
        }
    }, []);

    useEffect(() => {
        const onPaste = e => {
            if (e.clipboardData?.files) {
                e.preventDefault();
                e.stopPropagation();
                uploadFiles(e.clipboardData.files);
            }
        };
        if (!isUploading) {
            document.addEventListener('paste', onPaste);
        }
        return () => {
            document.removeEventListener('paste', onPaste);
        };
    }, [isUploading, uploadFiles]);

    useEffect(() => {
        return () => {
            clearTimeout(timer.current);
        };
    }, []);

    const onFileChange = async (
        fileData: AttachmentType = null,
        fileName: string,
        index: number
    ) => {
        const curForm = formsStore.getActiveForm();
        const FileFieldsEnum =
            curForm.parentForm.ename === 'MAILBOXSEND' ? MailboxSendFields : FileFields;
        const fileColumn = curForm.getColumnByName(FileFieldsEnum.fileData);
        const record = curForm.records[curForm.records.length - 1];
        curForm.setCurrentRow(record.recordIndex);

        let isSuccess = APIActionStatus.passed;

        if (curForm.parentForm.ename !== 'MAILBOXSEND') {
            const nameColumn: Column = curForm.getColumnByName(FileFields.fileName);
            const field: Field = record.fieldsMap.get(nameColumn.id);
            field.setCurrentValue(fileName);
            isSuccess = await fieldUpdate(
                record.recordIndex,
                mapValueByColumnType(fileName, nameColumn)
            );
        }

        if (isSuccess) {
            //upload file
            isSuccess = await saveRow(fileColumn, record, {
                ColumnID: fileColumn.ID,
                Value: new common.Constant({
                    File: fileData
                })
            });
        }
        if (!isSuccess) {
            await undoAll(formsStore.currentScreen);
        } else {
            successFilesCount.current++;
            record.isRecent = true;
            setParentNeedsRefresh(true);
        }
        if (index === filesCount.current - 1) {
            clearTimeout(timer.current);
            if (successFilesCount.current > 0) {
                setIsSuccess(true);
                timer.current = setTimeout(() => {
                    setIsUploading(false);
                    setIsSuccess(false);
                }, 3000);
            } else {
                setIsUploading(false);
            }
        }
    };

    //TODO: change the prop searchableColumns of SearchBlock so it will send the displayed fields.
    return (
        <div ref={contentWrapperRef} className={styles.contentWrapper}>
            <FileUploaderController
                setFile={uploadFiles}
                title={texts.drop_here}
                componentClasses={styles.dropZoneWrap}
                monitor={{send: true, text: 'AttachmentsDropFiles'}}
            >
                {(files?.length || !!fastSearchQuery) && (
                    <SearchBlock
                        searchQuery={searchQuery}
                        onSearchChange={setSearchQuery}
                        isServerSearch={true}
                        records={files}
                        defaultCols={Object.values(FileFields)}
                        enabledCols={enabledCols}
                        applySettings={applySettings}
                        subformType={subformType}
                    />
                )}
                {files?.length > 0 ? (
                    <>
                        <Scrollbar
                            onScrollEnd={handleScrollEnd}
                            componentClasses={styles.scrollbar}
                        >
                            {files?.map((file, ind) => {
                                return (
                                    <FileCard
                                        key={ind}
                                        file={file}
                                        searchQuery={searchQuery}
                                        containerRef={contentWrapperRef}
                                        enabledCols={enabledCols}
                                        setIsDownloading={setIsDownloading}
                                        setParentNeedsRefresh={setParentNeedsRefresh}
                                    />
                                );
                            })}
                        </Scrollbar>
                    </>
                ) : (
                    <EmptyState uploadFiles={uploadFiles} isFastSearch={!!fastSearchQuery} />
                )}
                <FileUploader />
                {(isUploading || isDownloading) && (
                    <ProgressBar
                        isSuccess={isSuccess}
                        isUploading={isUploading}
                        filesCount={filesCount.current}
                        successFilesCount={successFilesCount.current}
                    />
                )}
            </FileUploaderController>
        </div>
    );
};
